import { useFirestoreCollection } from "@p0security/frontend-core/react";
import { IntegrationConfig } from "@p0security/shared/integrations/shared";
import {
  directories,
  resourceIntegrations,
} from "components/Integrations/constants";
import { IntegrationData } from "components/Integrations/types";
import { getInstallCounts } from "components/Integrations/utils/getInstallCounts";
import { useFlags } from "launchdarkly-react-client-sdk";
import { cloneDeep } from "lodash";
import React, { createContext, useContext, useMemo } from "react";

type InstalledIntegrationsContextValue = {
  integrationMap: IntegrationData[];
  installedResources: IntegrationData[];
};

const InstalledIntegrationsContext = createContext<
  InstalledIntegrationsContextValue | undefined
>(undefined);

/** Page-level cache for integration install state.
 *
 * ResourcePreview is rendered many times (table cells, dropdown rows).
 * This provider ensures we only subscribe once to the `integrations` collection.
 */
export const InstalledIntegrationsProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const flags = useFlags();

  const integrationMap = useMemo(
    () => [
      ...cloneDeep(resourceIntegrations(flags)),
      ...cloneDeep(directories(flags)),
    ],
    [flags]
  );

  const { docs: installationConfigs } =
    useFirestoreCollection<IntegrationConfig>("integrations", {
      tenantAware: true,
    });

  const installations = useMemo(
    () =>
      Object.fromEntries(
        (installationConfigs ?? []).map((c) => [c.id, c.data])
      ),
    [installationConfigs]
  );

  const installedResources = useMemo(
    () =>
      integrationMap.filter(
        (i) => getInstallCounts(i, installations[i.key]).installed > 0
      ),
    [integrationMap, installations]
  );

  const value = useMemo(
    () => ({ integrationMap, installedResources }),
    [integrationMap, installedResources]
  );

  return (
    <InstalledIntegrationsContext.Provider value={value}>
      {children}
    </InstalledIntegrationsContext.Provider>
  );
};

/** Read installed integration state computed by InstalledIntegrationsProvider. */
export const useInstalledIntegrations =
  (): InstalledIntegrationsContextValue => {
    const ctx = useContext(InstalledIntegrationsContext);
    // add fallback
    return ctx ?? { integrationMap: [], installedResources: [] };
  };
